import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';

/**
 * Ticket status types - normalized internal status values
 */
export type TicketStatusType = 'new' | 'scheduled' | 'in_progress' | 'on_hold' | 'pending' | 'completed' | 'cancelled';

/**
 * Status information object returned by getTicketStatusInfo
 */
export interface TicketStatusInfo {
  /** Normalized status type */
  status: TicketStatusType;
  /** Human-readable label for display */
  label: string;
  /** Progress level (1-3) for progress bars */
  progress: 1 | 2 | 3;
  /** Background color for status badge */
  color: string;
  /** Whether the ticket is in a completed/closed state */
  isCompleted: boolean;
}

/**
 * Status color constants
 */
export const TICKET_STATUS_COLORS = {
  new: '#2563eb',         // blue-600 - New, Call, Web
  scheduled: '#7c3aed',   // violet-600 - Scheduled
  in_progress: '#ea580c', // orange-600 - In Progress
  on_hold: '#ca8a04',     // yellow-600 - On Hold
  pending: '#0891b2',     // cyan-600 - Request Reassignment/Pending
  completed: '#6b7280',   // gray-500 - Work Completed
  cancelled: '#6b7280',   // gray-500 - Canceled
  progress_active: '#66CE66', // green for progress bar
} as const;

/**
 * Get comprehensive status information for a maintenance ticket
 * Maps Yardi status values to normalized display statuses.
 * 
 * Yardi Standard Statuses:
 * - Call, Web -> New
 * - Scheduled -> Scheduled
 * - In Progress -> In Progress
 * - On Hold -> On Hold
 * - Request Reassignment -> Pending
 * - Work Completed -> Completed
 * - Canceled -> Cancelled
 * 
 * Yardi Custom Statuses (from GetCustomValues.xml):
 * - Parts Pending -> On Hold
 * - Resident Appointment -> Scheduled
 * - Awaiting Government Approval -> Pending
 * 
 * @param ticket - The maintenance ticket response
 * @returns TicketStatusInfo object with status, label, progress, color, and isCompleted
 * 
 * @example
 * const statusInfo = getTicketStatusInfo(ticket);
 * // { status: 'in_progress', label: 'In Progress', progress: 2, color: '#ea580c', isCompleted: false }
 */
export const getTicketStatusInfo = (ticket: IMaintenanceListResponse): TicketStatusInfo => {
  const status = (ticket.maintenanceStatus || '').toLowerCase().trim();

  // Completed state - handles "Work Completed" from Yardi and "completed"
  // Also check completedOn as fallback for synced tickets
  if (status === 'completed' || status === 'work completed' || !!ticket.completedOn) {
    return {
      status: 'completed',
      label: 'Completed',
      progress: 3,
      color: TICKET_STATUS_COLORS.completed,
      isCompleted: true,
    };
  }

  // Cancelled state - handles both "Canceled" (Yardi) and "Cancelled" spellings
  if (status === 'cancelled' || status === 'canceled') {
    return {
      status: 'cancelled',
      label: 'Cancelled',
      progress: 3,
      color: TICKET_STATUS_COLORS.cancelled,
      isCompleted: true,
    };
  }

  // Scheduled state - Yardi "Scheduled" and "Resident Appointment" custom status
  if (status === 'scheduled' || status === 'resident appointment') {
    return {
      status: 'scheduled',
      label: 'Scheduled',
      progress: 2,
      color: TICKET_STATUS_COLORS.scheduled,
      isCompleted: false,
    };
  }

  // In progress state (handles various formats)
  if (status === 'in progress' || status === 'inprogress' || status === 'in-progress') {
    return {
      status: 'in_progress',
      label: 'In Progress',
      progress: 2,
      color: TICKET_STATUS_COLORS.in_progress,
      isCompleted: false,
    };
  }

  // On Hold state - Yardi "On Hold" and "Parts Pending" custom status
  if (status === 'on hold' || status === 'onhold' || status === 'on-hold' || status === 'parts pending') {
    return {
      status: 'on_hold',
      label: 'On Hold',
      progress: 2,
      color: TICKET_STATUS_COLORS.on_hold,
      isCompleted: false,
    };
  }

  // Pending/Request Reassignment state - Yardi "Request Reassignment" and "Awaiting Government Approval" custom status
  if (
    status === 'request reassignment' || 
    status === 'pending' || 
    status === 'reassignment' ||
    status === 'awaiting government approval'
  ) {
    return {
      status: 'pending',
      label: 'Pending',
      progress: 2,
      color: TICKET_STATUS_COLORS.pending,
      isCompleted: false,
    };
  }

  // New state - Yardi "Call" and "Web" statuses indicate new tickets
  // Also the default for unknown statuses
  return {
    status: 'new',
    label: 'New',
    progress: 1,
    color: TICKET_STATUS_COLORS.new,
    isCompleted: false,
  };
};

/**
 * Get the progress level (1-3) for a ticket's status
 * Useful for rendering 3-segment progress bars
 * 
 * @param ticket - The maintenance ticket response
 * @returns Progress level: 1 (new), 2 (in progress), 3 (completed/cancelled)
 */
export const getStatusProgress = (ticket: IMaintenanceListResponse): 1 | 2 | 3 => {
  return getTicketStatusInfo(ticket).progress;
};

/**
 * Check if a ticket is in a completed/closed state
 * 
 * @param ticket - The maintenance ticket response
 * @returns true if completed or cancelled
 */
export const isTicketCompleted = (ticket: IMaintenanceListResponse): boolean => {
  return getTicketStatusInfo(ticket).isCompleted;
};

/**
 * Get the status label for display
 * 
 * @param ticket - The maintenance ticket response
 * @returns Human-readable status label
 */
export const getStatusLabel = (ticket: IMaintenanceListResponse): string => {
  return getTicketStatusInfo(ticket).label;
};

/**
 * Get the status badge background color
 * 
 * @param ticket - The maintenance ticket response
 * @returns Hex color string for the status badge
 */
export const getStatusColor = (ticket: IMaintenanceListResponse): string => {
  return getTicketStatusInfo(ticket).color;
};
